import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, X, Plus } from 'lucide-react';

export default function CustomerTagsPanel({ user, onSaved }) {
  const qc = useQueryClient();
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const current = user.tags || [];

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['customer-tags'],
    queryFn: () => base44.entities.CustomerTag.list('sort_order', 200),
  });

  const save = async (next) => {
    setBusy(true);
    const res = await base44.functions.invoke('adminUpdateUser', { userId: user.id, data: { tags: next } });
    setBusy(false);
    if (!res.data?.error) {
      onSaved?.({ tags: next });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    }
  };

  const toggle = (key) => {
    const next = current.includes(key) ? current.filter((t) => t !== key) : [...current, key];
    save(next);
  };

  const createTag = async () => {
    if (!newKey || !newLabel) return;
    const key = newKey.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    await base44.entities.CustomerTag.create({ key, label: newLabel, color: '#c0392b', active: true, sort_order: 0 });
    qc.invalidateQueries({ queryKey: ['customer-tags'] });
    setNewKey('');
    setNewLabel('');
    save([...current, key]);
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">APPLIED TAGS</p>
        {current.length === 0 ? (
          <p className="text-white/30 text-xs">No tags applied.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {current.map((k) => {
              const def = tags.find((t) => t.key === k);
              const color = def?.color || '#c0392b';
              return (
                <span key={k} className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2.5 py-1 border" style={{ color, borderColor: color + '55' }}>
                  {def?.label || k}
                  <button onClick={() => toggle(k)} disabled={busy} className="hover:opacity-70"><X className="w-3 h-3" /></button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">AVAILABLE TAGS</p>
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-white/40" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.filter((t) => t.active).map((t) => {
              const applied = current.includes(t.key);
              return (
                <button key={t.id} onClick={() => toggle(t.key)} disabled={busy || applied}
                  className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2.5 py-1 border transition-colors ${applied ? 'opacity-40 cursor-default' : 'hover:bg-white/5'}`}
                  style={{ color: t.color, borderColor: t.color + '55' }}>
                  {!applied && <Plus className="w-3 h-3" />}{t.label}
                </button>
              );
            })}
            {tags.length === 0 && <p className="text-white/30 text-xs">No tag definitions yet — create one below.</p>}
          </div>
        )}
      </div>

      <div className="border-t border-white/5 pt-4">
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">CREATE NEW TAG</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="key (e.g. vip)" className="flex-1 bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:border-primary/60 outline-none" />
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (e.g. VIP)" className="flex-1 bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:border-primary/60 outline-none" />
          <button onClick={createTag} disabled={busy || !newKey || !newLabel} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2 disabled:opacity-40">CREATE &amp; APPLY</button>
        </div>
      </div>
    </div>
  );
}